import React, { useState, useCallback, useRef } from "react";
import Link from "next/link";
import { ChevronDown, ChevronRight } from "lucide-react";

const Navbar = ({ isMobile, onClose }) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const timeoutRef = useRef(null);

  const menuItems = [
    {
      name: "Our BestSellings",
      icon: "❤️",
      path: "/mainHome/bestSelling",
      highlighted: true,
    },
    {
      name: "Customized Frames",
      icon: "🖼️",
      items: [
        { name: "Acrylic Frames", path: "/products/acrylic-frames" },
        { name: "Acrylic Plaques", path: "/products/acrylic-plaques" },
        { name: "Photo Frame", path: "/products/photo-frame" },
        { name: "Illustration Frames", path: "/products/illustration-frames" },
        { name: "Spotify Frame", path: "/products/spotify-frame" },
        { name: "Customised Canvas", path: "/products/canvas" },
      ],
    },
    {
      name: "Customized Product",
      icon: "🎨",
      items: [
        { name: "Acrylic Cassette", path: "/products/acrylic-cassette-transparent" },
        { name: "Customized Portrait", path: "/products/customized-portrait" },
        { name: "Wooden Portrait", path: "/products/wooden-portrait" },
        { name: "Wallet Card", path: "/products/wallet-card" },
        { name: "Bottle", path: "/products/bottle" },
        { name: "Phone Stand", path: "/products/phone-stand" },
        { name: "Photo Booth Strips", path: "/products/photo-booth-strips" },
      ],
    },
    {
      name: "Scannable Spotify Product",
      icon: "🎵",
      items: [
        { name: "Spotify Frame", path: "/products/spotify-frame" },
        { name: "Spotify Keychain", path: "/products/keychain" },
        { name: "Acrylic Cassette", path: "/products/acrylic-cassette-transparent" },
      ],
    },
    {
      name: "Standardized Product",
      icon: "📦",
      items: [
        { name: "Canvas", path: "/products/standard-canvas" },
        { name: "Anime Phone Stand", path: "/products/mobile-phone-stand" },
        { name: "Affirmation Mirrors", path: "/products/affirmation-mirror" },
        { name: "Acrylic Bookmarks", path: "/products/bookmarks" },
        { name: "Bottle", path: "/products/bottle" },
        { name: "Acrylic Keychain", path: "/products/acrylic-keychain" }
      ],
    },
  ];

  const handleLinkClick = useCallback((e) => {
    if (isMobile) {
      onClose?.();
    }
  }, [isMobile, onClose]);

  const handleMouseEnter = useCallback((itemName) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (!isMobile) {
      setActiveDropdown(itemName);
    }
  }, [isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobile) {
      timeoutRef.current = setTimeout(() => {
        setActiveDropdown(null);
      }, 150);
    }
  }, [isMobile]);

  const toggleDropdown = useCallback((itemName) => {
    setActiveDropdown((current) => (current === itemName ? null : itemName));
  }, []);

  return (
    <nav className="bg-white shadow-sm relative" role="navigation">
      <div className={`${isMobile ? "py-2" : "max-w-7xl mx-auto px-4"}`}>
        <div
          className={`${
            isMobile 
              ? "flex flex-col space-y-2" 
              : "flex items-center justify-center gap-4 h-14"
          }`}
        >
          {menuItems.map((item, index) => (
            <div
              key={item.name}
              className={`relative group ${isMobile ? "w-full" : "inline-block"}`}
              onMouseEnter={() => handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                href={item.path || "#"}
                className={`
                  flex items-center justify-between
                  ${isMobile ? "w-full px-4 py-3" : "px-3 py-2"}
                  ${item.highlighted
                    ? "bg-red-50 text-red-600 font-semibold animate-pulse"
                    : "text-gray-700 hover:text-red-600 hover:bg-red-50/30"
                  }
                  rounded-lg transition-all duration-200 ease-in-out
                  ${isMobile ? "text-base" : "text-sm"}
                  ${activeDropdown === item.name ? "text-red-600 bg-red-50/50" : ""}
                `}
                onClick={(e) => {
                  if (isMobile && item.items) {
                    e.preventDefault();
                    toggleDropdown(item.name);
                  } else {
                    handleLinkClick(e);
                  }
                }}
              >
                <span className="flex items-center gap-2">
                  <span className="text-lg">{item.icon}</span>
                  {item.name}
                </span>
                {item.items && (
                  <span className="ml-1">
                    {isMobile ? (
                      <ChevronRight
                        className={`h-5 w-5 transition-transform duration-200 ${
                          activeDropdown === item.name ? "rotate-90" : ""
                        }`}
                      />
                    ) : (
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${
                          activeDropdown === item.name ? "rotate-180" : ""
                        }`}
                      />
                    )}
                  </span>
                )}
              </Link>

              {item.items && (
                <div
                  className={`
                    ${isMobile 
                      ? "mt-1 mb-2 px-4" 
                      : `absolute top-full ${index >= menuItems.length - 2 ? 'right-0' : 'left-0'} min-w-[250px] bg-white shadow-lg rounded-lg`
                    }
                    ${
                      activeDropdown === item.name
                        ? isMobile
                          ? "max-h-[500px] opacity-100"
                          : "opacity-100 visible translate-y-0"
                        : isMobile
                        ? "max-h-0 opacity-0"
                        : "opacity-0 invisible translate-y-2"
                    }
                    transition-all duration-200 ease-in-out overflow-hidden z-50
                  `}
                >
                  <div
                    className={`
                      ${isMobile ? "bg-gray-50 rounded-lg" : "py-1 bg-white"}
                      border border-gray-100
                    `}
                  >
                    {item.items.map((subItem, subIndex) => (
                      <Link
                        key={subItem.name}
                        href={subItem.path}
                        onClick={handleLinkClick}
                        className={`
                          block
                          ${isMobile ? "px-6 py-3.5" : "px-4 py-2.5"}
                          text-gray-600 hover:text-red-600 hover:bg-red-50
                          transition-colors duration-200 text-sm
                          ${subIndex !== 0 && "border-t border-gray-100/50"}
                        `}
                      >
                        {subItem.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          <div
            className={`
              ${
                isMobile
                  ? "mt-6 pt-4 space-y-4 border-t border-gray-200 px-4"
                  : "flex items-center gap-6"
              }
            `}
          >
            {["About Us", "Contact", "Blog"].map((item) => (
              <Link
                key={item}
                href={`/${item.toLowerCase().replace(" ", "-")}`}
                className={`
                  ${
                    isMobile
                      ? "block py-3 px-4 bg-gray-50/50 rounded-lg"
                      : "inline-flex items-center px-3 py-2 rounded-md"
                  }
                  text-gray-700 hover:text-red-600 hover:bg-red-50
                  transition-colors duration-200 font-medium text-sm
                  ${item !== "Blog" && "md:hidden"}
                `}
                onClick={handleLinkClick}
              >
                {item}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;